"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, type CSSProperties } from "react";
import { CircleCheckIcon, InfoIcon, Loader2Icon, OctagonXIcon, TriangleAlertIcon } from "lucide-react";
import { Toaster as Sonner, toast, useSonner, type ToasterProps, type ToastT } from "sonner";
const TRACKPAD_SWIPE_DISMISS_THRESHOLD = 80;
const TRACKPAD_SWIPE_RESET_DELAY = 240;
const toastIconClassName = "inline-flex size-7 shrink-0 items-center justify-center rounded-full";
const toastSvgClassName = "size-3.5";
const getToastIdFromElement = (element: HTMLElement, toasts: ToastT[]) => {
  const toastIndex = Number(element.dataset.index);
  const toastPosition = `${element.dataset.yPosition}-${element.dataset.xPosition}`;
  if (!Number.isInteger(toastIndex)) {
    return undefined;
  }
  return toasts.filter(candidate => !candidate.position || candidate.position === toastPosition)[toastIndex]?.id;
};
const Toaster = t0 => {
  const $ = _c(18);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  const {
    toasts
  } = useSonner();
  const toastsRef = useRef(toasts);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      deltaX: 0,
      element: null,
      resetTimer: null
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const trackpadSwipeRef = useRef(t1);
  let t2;
  let t3;
  if ($[3] !== toasts) {
    t2 = () => {
      toastsRef.current = toasts;
    };
    t3 = [toasts];
    $[3] = toasts;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const resetTrackpadSwipe = () => {
        if (trackpadSwipeRef.current.resetTimer) {
          clearTimeout(trackpadSwipeRef.current.resetTimer);
        }
        trackpadSwipeRef.current = {
          deltaX: 0,
          element: null,
          resetTimer: null
        };
      };
      const handleTrackpadSwipe = event => {
        const target = event.target instanceof Element ? event.target : null;
        const toastElement = target?.closest("[data-sonner-toast]");
        if (!toastElement || toastElement.dataset.dismissible === "false") {
          return;
        }
        const horizontalDelta = Math.abs(event.deltaX);
        const verticalDelta = Math.abs(event.deltaY);
        if (horizontalDelta <= verticalDelta || horizontalDelta < 4) {
          return;
        }
        event.preventDefault();
        if (trackpadSwipeRef.current.element !== toastElement) {
          resetTrackpadSwipe();
          trackpadSwipeRef.current.element = toastElement;
        }
        trackpadSwipeRef.current.deltaX = trackpadSwipeRef.current.deltaX + event.deltaX;
        if (Math.abs(trackpadSwipeRef.current.deltaX) >= TRACKPAD_SWIPE_DISMISS_THRESHOLD) {
          const toastId = getToastIdFromElement(toastElement, toastsRef.current);
          if (toastId !== undefined) {
            toast.dismiss(toastId);
          }
          resetTrackpadSwipe();
          return;
        }
        if (trackpadSwipeRef.current.resetTimer) {
          clearTimeout(trackpadSwipeRef.current.resetTimer);
        }
        trackpadSwipeRef.current.resetTimer = setTimeout(resetTrackpadSwipe, TRACKPAD_SWIPE_RESET_DELAY);
      };
      document.addEventListener("wheel", handleTrackpadSwipe, {
        passive: false
      });
      return () => {
        document.removeEventListener("wheel", handleTrackpadSwipe);
        resetTrackpadSwipe();
      };
    };
    t5 = [];
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = ["left", "right"];
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className={`${toastIconClassName} bg-emerald-500/10 text-emerald-600`}><CircleCheckIcon className={toastSvgClassName} /></span>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className={`${toastIconClassName} bg-primary/10 text-primary`}><InfoIcon className={toastSvgClassName} /></span>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className={`${toastIconClassName} bg-yellow-500/10 text-yellow-700`}><TriangleAlertIcon className={toastSvgClassName} /></span>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className={`${toastIconClassName} bg-destructive/10 text-destructive`}><OctagonXIcon className={toastSvgClassName} /></span>;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      success: t7,
      info: t8,
      warning: t9,
      error: t10,
      loading: <span className={`${toastIconClassName} bg-primary/10 text-primary`}><Loader2Icon className={`${toastSvgClassName} animate-spin`} /></span>
    };
    t12 = {
      "--normal-bg": "var(--popover)",
      "--normal-text": "var(--popover-foreground)",
      "--normal-border": "var(--border)",
      "--border-radius": "var(--radius)"
    };
    $[13] = t11;
    $[14] = t12;
  } else {
    t11 = $[13];
    t12 = $[14];
  }
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      classNames: {
        toast: "cn-toast rounded-2xl border-border bg-card/95 p-3 text-card-foreground shadow-2xl backdrop-blur-xl",
        content: "gap-1",
        title: "text-sm font-medium leading-5",
        description: "text-xs leading-5 text-muted-foreground",
        icon: "mt-0.5",
        actionButton: "rounded-full bg-primary px-3 text-xs font-medium text-primary-foreground",
        cancelButton: "rounded-full bg-muted px-3 text-xs font-medium text-muted-foreground",
        closeButton: "rounded-full border-border bg-card text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      }
    };
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== props) {
    t14 = <Sonner theme="light" className="toaster group" closeButton={true} position="bottom-right" visibleToasts={4} gap={10} offset={24} swipeDirections={t6} icons={t11} style={t12 as CSSProperties} toastOptions={t13} {...props} />;
    $[16] = props;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  return t14;
};
export { Toaster };